import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NoteDTO, REACTIONS } from "@retrospecter/shared/types";
import { apiPost } from "../../api/client";
import { useTranslation } from "../../i18n";

interface NoteReactionsProps {
  note: NoteDTO;
  /** Whether this viewer may react (a participant on a live board). */
  canReact: boolean;
  onChanged: () => void;
}

/** Rough size of the picker, used to decide whether it fits below. */
const MENU_WIDTH = 200;
const MENU_HEIGHT = 44;
const MENU_GAP = 6;

export function NoteReactions({ note, canReact, onChanged }: NoteReactionsProps) {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [anchor, setAnchor] = useState<{ left: number; top: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  /**
   * Positions the picker against the trigger in viewport coordinates.
   *
   * It renders in a portal because the column card clips its overflow — an
   * absolutely-positioned popover was sliced off at the bottom of the card.
   * Flips above the button when there is no room below.
   */
  const place = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const fitsBelow =
      window.innerHeight - rect.bottom > MENU_HEIGHT + MENU_GAP;

    setAnchor({
      left: Math.max(
        8,
        Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8)
      ),
      top: fitsBelow
        ? rect.bottom + MENU_GAP
        : rect.top - MENU_HEIGHT - MENU_GAP,
    });
  };

  useLayoutEffect(() => {
    if (picking) place();
  }, [picking]);

  // Close the picker on an outside click or Escape, like any popover.
  useEffect(() => {
    if (!picking) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setPicking(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPicking(false);
    };
    // Fixed positioning does not follow the page, so close rather than drift.
    const onReflow = () => setPicking(false);

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [picking]);

  const toggle = async (key: string) => {
    setBusy(true);
    try {
      await apiPost(`/api/notes/${note.id}/reactions`, { key });
      setPicking(false);
      onChanged();
    } catch {
      // The board refetches regardless; a failed reaction is not worth an alert.
    } finally {
      setBusy(false);
    }
  };

  // Nothing to show and nothing to add: stay out of the way entirely.
  if (note.reactions.length === 0 && !canReact) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1" ref={containerRef}>
      {note.reactions.map((reaction) => (
        <button
          key={reaction.key}
          onClick={() => toggle(reaction.key)}
          disabled={!canReact || busy}
          aria-pressed={reaction.mine}
          aria-label={
            reaction.mine
              ? t("note.unreact", { emoji: reaction.emoji })
              : t("note.react", { emoji: reaction.emoji })
          }
          className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs transition-colors ${
            reaction.mine
              ? "border-nm-accent/40 bg-nm-accent/12 text-ink"
              : "border-line bg-surface-sunken text-ink-soft"
          } ${canReact ? "hover:border-nm-accent/50" : "cursor-default"}`}
        >
          <span aria-hidden="true">{reaction.emoji}</span>
          <span className="tabular-nums">{reaction.count}</span>
        </button>
      ))}

      {canReact && (
        <>
          <button
            ref={triggerRef}
            onClick={() => setPicking((open) => !open)}
            aria-label={t("note.addReaction")}
            aria-expanded={picking}
            data-testid="add-reaction"
            className="flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-line-strong text-xs text-ink-faint transition-colors hover:border-nm-accent/50 hover:text-nm-accent"
          >
            <span aria-hidden="true">+</span>
          </button>

          {picking &&
            anchor &&
            createPortal(
              <div
                ref={menuRef}
                role="menu"
                style={{ left: anchor.left, top: anchor.top }}
                className="animate-popIn fixed z-50 flex gap-0.5 rounded-xl border border-line bg-surface p-1 shadow-float"
              >
                {REACTIONS.map((reaction) => (
                  <button
                    key={reaction.key}
                    role="menuitem"
                    onClick={() => toggle(reaction.key)}
                    disabled={busy}
                    aria-label={t("note.react", { emoji: reaction.emoji })}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-base transition-transform hover:scale-125 hover:bg-surface-sunken"
                  >
                    <span aria-hidden="true">{reaction.emoji}</span>
                  </button>
                ))}
              </div>,
              document.body
            )}
        </>
      )}
    </div>
  );
}
